import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import OrderSummary from './order-summary/OrderSummary'
import SecureCheckoutForm from './SecureCheckoutForm'
import * as API from '../../../src/util/API'
import * as GTM from '../../../src/util/GTM'
import CreatingYourAccount from './CreatingYourAccount'
import { AccountDetails, LeadParams } from '../../types'
import { injectStripe, Stripe } from 'react-stripe-elements'
import ReCAPTCHA from 'react-google-recaptcha'
import { ValidationError } from '../../util/Validations'
import { createStyles, makeStyles } from '@material-ui/core'
import AccountCreated from './AccountCreated'
import Spinner from 'spinner'
import orderSummaryContext from './order-summary/OrderSummaryContext'
import OrderLayout, { OrderLayoutLeftColumn, OrderLayoutRightColumn } from '../OrderLayout'
import LeadContext, { LeadContextValue } from '../../LeadContext'
import SubmitButton from '../../design-components/SubmitButton'
import { Alert as FoundationsAlert } from 'foundations-library/components'
import BusinessInfoForm from './BusinessInfoForm'
import { useHistory } from 'react-router-dom'
import TermsAndConditions from './TermsAndConditions'
import MFA from 'mfa-widget'

const useStyles = makeStyles(theme =>
    createStyles({
        form: {
            margin: '0 auto',
            maxWidth: '850px'
        },
        submitContainer: {
            width: '100%',
            marginTop: '30px',
            marginBottom: '20px'
        },
        recaptchaContainer: {
            position: 'relative',
            zIndex: 1
        }
    })
)

interface CheckoutProps {
    stripe: Stripe;
    setShowStepper: (showStepper: boolean) => void;
    setDisableStepper: (disableStepper: boolean) => void;
}

/**
 * Checkout step of sign-up
 */
const Checkout = ({ stripe, setShowStepper, setDisableStepper } : CheckoutProps) : JSX.Element => {
    const classes = useStyles()
    const history = useHistory()

    const { saveLeadParams, leadParams, invalidateSelectedNumber, performMFASteps } = useContext<LeadContextValue>(LeadContext)

    const orderSummary = useContext(orderSummaryContext)
    const { bill } = orderSummary

    const [error, setError] = useState<Error | null>(null)
    const [processing, setProcessing] = useState(false)
    const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false)
    const [accountDetails, setAccountDetails] = useState<AccountDetails | null>(null)

    const [agreeWithSubscription, setAgreeWithSubscription] = useState<boolean>(false)
    const [receiveSMS, setReceiveSMS] = useState<boolean>(false)
    const [receiveSMSOffers, setReceiveSMSOffers] = useState<boolean>(false)

    const recaptchaRef = useRef()
    const mfaRef = useRef<MFA | null>(null)

    // useEffect(() => {
    //     const searchParams = new URLSearchParams(window.location.search)
    //     if (searchParams.get('returned_from_mfa')) {
    //         console.info('MFA: User has returned from the MFA app, starting the account creation process...')
    //         createAccount()
    //     }
    // }, [])

    useEffect(() => {
        if (mfaRef.current) return

        const mfa = new MFA()
        mfa.init(
            {
                MFASessionCompleted: () => {
                    console.info('MFA: completed, continuing account creation...')
                    createAccount()
                },
                MFASessionFailed: () => {
                    onError(new Error('Verification failed. Please try again.'))
                },
                MFAClosed: () => {
                    console.info('MFA: closed')
                    setProcessing(false)
                }
            },
            { stage: 'stage' } // ✅ IMPORTANT
        )
        mfaRef.current = mfa
        return () => {
            try { mfa.deactivate?.() } catch {}
        }
    }, [])

    const buildMFAReturnURL = () => {
        const currentParams = new URLSearchParams(window.location.search)
        const returnURLParams = new URLSearchParams({
            returned_from_mfa: 'true',
            session_token: currentParams.get('session_token') || ''
        })
        return `https://${window.location.host}/checkout?${returnURLParams.toString()}`
    }

    const onSubmit = async event => {
        event.preventDefault()

        if (processing) {
            return // Prevent submitting again by pressing enter, etc.
        }

        setError(null) // clear any previous error
        setProcessing(true)

        try {
            const tasks = [
                { fn: () => saveLead(), description: 'saving lead data' },
                { fn: () => savePaymentToken(), description: 'payment information processing' }
            ]

            if (leadParams.enable_recaptcha) {
                tasks.push(
                    { fn: () => verifyHumanUser(), description: 'human verification' }
                )
            }

            for (const task of tasks) {
                await task.fn().catch(error => {
                    if (error instanceof ValidationError) {
                        GTM.validationError(error.message, error.errorCode)
                    } else {
                        GTM.checkoutError(error.message, task.description)
                    }
                    throw error
                })
            }
            const canSkip = performMFASteps()
            if (canSkip) {
                console.info('MFA: skipping MFA setup and starting account creation process...')
                createAccount()
            } else {
                const mfaReturnURL = buildMFAReturnURL()
                console.log('MFA: starting MFA setup...', mfaReturnURL)
                const { session_key } = await API.getMFASessionKey(mfaReturnURL)
                console.log('MFA: key', session_key)
                if (!mfaRef.current) throw new Error('MFA widget not initialized')
                mfaRef.current.open(session_key, { width: '600px', height: '450px' })
            }
        } catch (error) {
            console.log('Error in onSubmit: ', error)
            onError(error)
        }
    }

    const onError = (error: Error) => {
        console.info('Submission error: ', error)

        if (error instanceof API.CreateAccountError) {
            const errorType = error.createAccountErrors[0].error_type
            if (errorType === 'Number Purchase Error') {
                invalidateSelectedNumber('number_not_available')
                history.push('/pick-a-number')
                return
            }
        }

        setError(error)
        setProcessing(false)
        setIsCreatingAccount(false)
        window.scrollTo(0, 0)
    }

    const saveLead = async () => {
        const currentParams = new URLSearchParams(window.location.search)
        const returnURLParams = new URLSearchParams({
            returned_from_mfa: 'true', // eslint-disable-line @typescript-eslint/naming-convention
            session_token: currentParams.get('session_token') || '' // eslint-disable-line @typescript-eslint/naming-convention
        })
        const mfaReturnURL = `https://${window.location.host}/checkout?${returnURLParams.toString()}`

        await saveLeadParams(
            [
                'company_name',
                'first_name',
                'last_name',
                'email',
                'personal_phone_number',
                'password',
                'confirmPassword',
                'street',
                'country',
                'billing_country_code',
                'state',
                'city',
                'billing_zip_code'
            ],
            mfaReturnURL
        )
    }

    const savePaymentToken = async () => {
        if (bill?.skip_credit_card_entry) {
            return
        }

        const result = await stripe.createToken({ type: 'card' })

        if (result.error) {
            throw result.error
        } else {
            const stripePaymentToken = result.token.id
            try {
                await API.createPaymentToken(stripePaymentToken)
            } catch (error) {
                if (error.message.includes('expired')) {
                    invalidateSelectedNumber('number_expired')
                    history.push('/pick-a-number')
                }
                throw error
            }
        }
    }

    // Starts the process of google recaptcha verifying the user is a human
    const verifyHumanUser = async () => {
        // Intentionally stop the "processing" state undisabling the UI including the submit button,
        // so that if the user dismisses the challenge modal (eg. clicking outside of it) they can restart the process by submitting again.
        setProcessing(false)
        GTM.captchaStarted()

        const onError = () => {
            throw new Error('There was a problem verifying you are a human, please refresh the page and try again')
        }

        if (!recaptchaRef.current) {
            console.error('Ref to recaptcha widget not set - can not execute recaptcha')
            onError()
        }

        let token
        try {
            token = await recaptchaRef.current.executeAsync()
        } catch (error) {
            console.error('Error while executing recaptcha: ', error)
            onError()
        }

        GTM.captchaSucceeded()
        setProcessing(true)

        let response
        try {
            response = await API.verifyRecaptcha(token)
        } catch (error) {
            console.error('Error saving recaptcha token: ', error)
            onError()
        }

        if (!response?.data?.success) {
            console.error('Non-successful response from backend while trying to save recaptcha token: ', response.data)
            onError()
        }
    }

    const onRecaptchaExpired = () => {
        console.info('ReCAPTCHA Expired')
        GTM.captchaExpired()
        onError(new Error('Your ReCAPTCHA session used to verify that your a human has expired, please try again'))
    }

    const onRecaptchaError = (error: Error) => {
        console.error(`ReCAPTCHA Error: ${error.name} ${error.message}`)
        GTM.captchaError(error)
        onError(new Error('An error occured while trying to verify that your a human, please try again'))
    }

    const createAccount = async () => {
        setIsCreatingAccount(true)
        try {
            const accountDetails = await API.createAccount({ receiveSMS, receiveSMSOffers })
            setAccountDetails(accountDetails)
        } catch (error) {
            onError(error)
            GTM.transactionError(error, 'account creation')
        }
    }

    useEffect(() => {
        setDisableStepper(processing || isCreatingAccount)
    }, [processing, isCreatingAccount])

    useEffect(() => {
        // For UX reasons we're hiding the Stepper when the account is created,
        // as there is no going back to previous steps at this point
        setShowStepper(!accountDetails)
    }, [accountDetails])
    const allFieldsValid = useCallback(() => {
        const requiredFields = [
            'first_name',
            'last_name',
            'company_name',
            'email',
            'personal_phone_number',
            'password',
            'confirmPassword',
            'street',
            'city',
            'state',
            'billing_zip_code',
            'billing_country_code',
            'country'
        ]
        for (const field of Object.keys(leadParams)) {
            if (requiredFields.includes(field) && leadParams[field as keyof LeadParams] === '') {
                console.error('field', field, 'is required but is empty')
                return false
            }
        }
        return true
    }, [leadParams])

    if (accountDetails) return <AccountCreated accountDetails={accountDetails}/>
    if (isCreatingAccount) return <CreatingYourAccount />

    console.log('allFieldsValid', allFieldsValid())

    return (
        <>
            <form id="checkout-form" name="checkout-form" className={classes.form} onSubmit={onSubmit}>
                {error &&
                    <FoundationsAlert data-testid="error-alert" autocloseAfter={5000} onClose={() => setError(null)} marginY={20}>
                        {error.message}
                    </FoundationsAlert>
                }

                <OrderLayout>
                    <OrderLayoutLeftColumn>
                        <BusinessInfoForm />
                        <SecureCheckoutForm
                            skipCreditCardEntry={bill?.skip_credit_card_entry || false}
                        />
                    </OrderLayoutLeftColumn>

                    <OrderLayoutRightColumn>
                        <OrderSummary
                            disabled={processing}
                        />

                        <TermsAndConditions
                            disabled={processing}

                            agreeWithSubscription={agreeWithSubscription}
                            setAgreeWithSubscription={setAgreeWithSubscription}

                            receiveSMS={receiveSMS}
                            setReceiveSMS={setReceiveSMS}

                            receiveSMSOffers={receiveSMSOffers}
                            setReceiveSMSOffers={setReceiveSMSOffers}
                        />

                        <div className={classes.submitContainer}>
                            <SubmitButton
                                disabled={!allFieldsValid() || Boolean(error) || processing || !bill || (bill?.coupon_code === 'error') || !receiveSMS || !agreeWithSubscription || !receiveSMSOffers}
                            >
                                {processing
                                    ? (
                                        <Spinner color="white" size="small" />
                                    )
                                    : (
                                        <>Create your account</>
                                    )}
                            </SubmitButton>
                        </div>

                        {/* <CheckoutBadges /> */}
                    </OrderLayoutRightColumn>
                </OrderLayout>
                <div className={classes.recaptchaContainer}>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        badge="bottomleft"
                        sitekey="6LcEHXAUAAAAALeg1BlVWZNcvo6LCOf_mwa8rh1P"
                        onError={onRecaptchaError}
                        onExpired={onRecaptchaExpired}
                    />
                </div>
            </form>
        </>
    )
}

export default injectStripe(Checkout)
